'use client';

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

import { useLatest } from 'react-use';

import { consumeUserThreadSwitch } from '@/lib/thread-switch-signal';

interface Params {
  threadId: string | undefined;
  isReadOnly: boolean;
  isRunning: boolean;
  viewportRef: RefObject<HTMLDivElement | null>;
  scrollToBottom: (options: { behavior: ScrollBehavior }) => void;
  onSettlingChange?: (settling: boolean) => void;
  /** The thread shows the welcome screen: no messages, and none being restored. */
  isWelcome?: boolean;
}

export interface ThreadSwitchBehavior {
  handleSettle: () => void;
  handleSubmit: () => void;
  handleScrollToBottom: () => void;
}

/**
 * Manages thread-switch scroll behavior and settling state.
 *
 * On thread switch:
 * - idle thread: triggers settling overlay, scrolls to bottom (not on the first load of the
 *   welcome screen, which the server already rendered in its final state)
 * - running thread: cancels overlay, scrolls to bottom
 * - read-only thread: triggers settling overlay, scrolls to top (no bottom scroll on settle)
 *
 * On message submit: no-op — the library's top-anchor mechanism
 * (turnAnchor="top" on ThreadPrimitive.Viewport) positions the user message
 * at the top of the viewport once the assistant response begins streaming.
 * Any competing scroll from this hook would fire in a later rAF and cancel
 * the top-anchor's scroll, collapsing the reserve and snapping to bottom.
 *
 * On run end: drops the library's pending scroll-to-bottom request unless the user
 * asked for the bottom during the run, and inserts a
 * compensating spacer element when the library's top-anchor reserve collapses,
 * preventing the browser from clamping scrollTop and snapping the user message away
 * from the top of the viewport.
 */
export function useThreadSwitchBehavior({
  threadId,
  isReadOnly,
  isRunning,
  viewportRef,
  scrollToBottom,
  onSettlingChange,
  isWelcome = false,
}: Params): ThreadSwitchBehavior {
  const [settling, setSettling] = useState(false);

  useEffect(() => {
    onSettlingChange?.(settling);
  }, [settling, onSettlingChange]);

  // Must be declared before the threadId layout effect so it runs first when
  // both fire in the same render (thread switch to a running thread).
  const isRunningRef = useRef(isRunning);
  // When isRunning goes true→false, record the timestamp so the threadId effect
  // can detect an *immediate* cloud ID migration (local→remote swap that happens
  // within a short window right after the run) vs. a later user-initiated switch.
  const runEndedAtRef = useRef<number | null>(null);
  // Set when the user asks for the bottom during a run, so the run-end cancel below keeps their request.
  const userScrolledToBottomRef = useRef(false);
  useLayoutEffect(() => {
    const prev = isRunningRef.current;
    if (prev && !isRunning) {
      runEndedAtRef.current = performance.now();
      // WORKAROUND: assistant-ui keeps a scroll-to-bottom request (from opening the thread) pending
      // while the content fits the viewport, and drops it only on an overflow while the top anchor
      // holds or on a gesture inside the viewport. A first
      // answer short enough to fit never overflows during the run, so the request outlives it and fires
      // on the next growth, the carousel mounting as the run ends, snapping the thread to the bottom.
      // A pointerdown on the viewport is the library's own cancel, and this layout effect runs before
      // its resize observer sees the growth. Drop this once assistant-ui clears that request when a
      // top-anchored run starts or ends (not the case through 0.15.22; see assistant-ui#7897).
      if (!userScrolledToBottomRef.current) {
        viewportRef.current?.dispatchEvent(new Event('pointerdown'));
      }
    } else if (!prev && isRunning) {
      runEndedAtRef.current = null;
      userScrolledToBottomRef.current = false;
      setSettling(false);
    }
    isRunningRef.current = isRunning;
  }, [isRunning, viewportRef]);

  // Latest-value refs so callbacks/effects read current values without taking
  // them as dependencies. (Unlike isRunningRef above, these don't need the
  // previous value, so useLatest's render-time sync is correct.)
  const isReadOnlyRef = useLatest(isReadOnly);
  const onSettlingChangeRef = useLatest(onSettlingChange);
  // The cover hides a switch's content swap and its jump to the bottom. On the first run the
  // server-rendered welcome is already the final view, so covering it only flashes it away.
  const skipFirstCoverRef = useRef(isWelcome);

  useLayoutEffect(() => {
    const runEndedAt = runEndedAtRef.current;
    runEndedAtRef.current = null;
    const wasJustRunning = runEndedAt !== null && performance.now() - runEndedAt < 500;

    // A user-initiated switch away from a running thread also flips isRunning
    // true→false as threadId changes — indistinguishable from a cloud-ID
    // migration by timing alone. consumeUserThreadSwitch() disambiguates: only a
    // real switch goes through emitThreadSwitchStart. Read-and-clear so it
    // applies to just this threadId change; a later migration's run sees false.
    const wasUserSwitch = consumeUserThreadSwitch();
    const skipCover = skipFirstCoverRef.current;
    skipFirstCoverRef.current = false;

    if (isReadOnly) {
      viewportRef.current?.scrollTo({ top: 0, behavior: 'instant' });
      setSettling(true);
    } else if (isRunningRef.current) {
      setSettling(false);
      scrollToBottom({ behavior: 'instant' });
      onSettlingChangeRef.current?.(false);
    } else if (wasJustRunning && !wasUserSwitch) {
      // cloud ID migration: skip scroll/overlay
    } else {
      if (!skipCover) setSettling(true);
      scrollToBottom({ behavior: 'instant' });
    }
  }, [threadId, isReadOnly, scrollToBottom, viewportRef, onSettlingChangeRef]);

  const settlingRef = useLatest(settling);

  const handleSettle = useCallback(() => {
    if (!settlingRef.current) return;
    if (!isReadOnlyRef.current) {
      scrollToBottom({ behavior: 'instant' });
    }
    setSettling(false);
  }, [scrollToBottom, settlingRef, isReadOnlyRef]);

  // The library's top-anchor reserve (turnAnchor="top") fires in the rAF
  // immediately after isRunning=true renders and scrolls the viewport to
  // position the user message at the top. Any scroll we do here fires in a
  // later rAF (double-rAF) and cancels the top-anchor's scroll, driving
  // the viewport to the content bottom and collapsing the reserve.
  const handleSubmit = useCallback(() => {}, []);

  const handleScrollToBottom = useCallback(() => {
    if (isRunningRef.current) userScrolledToBottomRef.current = true;
  }, []);

  // When the run ends, the library removes its top-anchor reserve element, causing
  // the browser to clamp scrollTop and snap the user message away from the top.
  // We insert a compensating spacer (same height as the reserve) in the MutationObserver
  // microtask — before the browser reflows — so scrollHeight stays the same and
  // scrollTop is not clamped. The spacer is removed on the next run start or thread switch.
  const prevIsRunningForSpacerRef = useRef(isRunning);
  useLayoutEffect(() => {
    const wasRunning = prevIsRunningForSpacerRef.current;
    prevIsRunningForSpacerRef.current = isRunning;

    if (isRunning || !wasRunning) return;

    const viewport = viewportRef.current;
    const reserveEl = viewport?.querySelector<HTMLElement>('[data-aui-top-anchor-reserve]');
    if (!reserveEl?.isConnected || reserveEl.offsetHeight === 0) return;

    const reserveHeight = reserveEl.offsetHeight;
    const reserveParent = reserveEl.parentElement;
    if (!reserveParent) return;

    const spacer = document.createElement('div');
    spacer.style.cssText = `height: ${reserveHeight}px; flex-shrink: 0; pointer-events: none;`;
    spacer.setAttribute('aria-hidden', 'true');

    let inserted = false;
    const observer = new MutationObserver(() => {
      if (!reserveEl.isConnected && !inserted) {
        inserted = true;
        observer.disconnect();
        reserveParent.appendChild(spacer);
      }
    });
    observer.observe(reserveParent, { childList: true });

    return () => {
      observer.disconnect();
      spacer.remove();
    };
  }, [isRunning, threadId, viewportRef]);

  return { handleSettle, handleSubmit, handleScrollToBottom };
}
